import React, { useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Box, Typography, Paper, Container, Alert, Divider, Fade, Grid, Button } from '@mui/material';
import { Facebook, Google, LockOutlined, Microsoft } from '@mui/icons-material';
import { authApi } from '../services/authApi';
import { useAppSelector } from '../store/hooks';
import { env } from '../config/env';
import FormProvider from '../components/form/FormProvider';
import TextField from '../components/form/TextField';
import PasswordField from '../components/form/PasswordField';
import { z } from 'zod';
import useZodForm from '../components/form/useZodForm';

const loginSchema = z.object({
  username: z.string().email('Invalid email'),
  password: z.string().min(8, 'Password must be at least 8 characters'),
});

const LoginPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from?.pathname || '/dashboard';
  
  const [login, { isLoading, error }] = authApi.endpoints.login.useMutation();
  const { isAuthenticated } = useAppSelector(state => state.auth);
  
  const methods = useZodForm({
    schema: loginSchema,
    defaultValues: {
      username: '',
      password: ''
    },
  });
  
  useEffect(() => {
    if (isAuthenticated) {
      navigate(from, { replace: true });
    }
  }, [isAuthenticated, navigate, from]);
  
  const onSubmit = async (data) => {
    try {
      await login({
        username: data.username,
        password: data.password
      }).unwrap();
    } catch (err) {
      console.error('Login error:', err);
    }
  };
  
  return (
    <Container maxWidth="sm">
      <Paper elevation={3} sx={{ p: 4, mt: 8 }}>
        <Box display="flex" flexDirection="column" alignItems="center" mb={3}>
          <Box 
            sx={{ 
              backgroundColor: 'primary.main', 
              borderRadius: '50%', 
              p: 1, 
              mb: 2 
            }}
          >
            <LockOutlined sx={{ color: 'white', fontSize: 30 }} />
          </Box>
          <Typography variant="h4" component="h1" align="center" gutterBottom>
            Sign In
          </Typography>
          <Typography variant="body2" color="text.secondary" align="center">
            Access your {env.appTitle} account
          </Typography>
        </Box>
        
        <Fade in={!!error}>
          <Box sx={{ mb: error ? 2 : 0 }}>
            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error.data?.message || 'Authentication failed. Please check your credentials.'}
              </Alert>
            )}
          </Box>
        </Fade>
        
        <FormProvider methods={methods} onSubmit={onSubmit} submitText="Sign In" isLoading={isLoading}>
          <Grid size={12}>
            <TextField name="username" label="Username or Email" required autoFocus disabled={isLoading} />
          </Grid>
          <Grid size={12}>
            <PasswordField name="password" label="Password" required disabled={isLoading} />
          </Grid>
        </FormProvider>
        
        <Divider sx={{ my: 2 }}>
          <Typography variant="body2" color="text.secondary">
            Other Providers
          </Typography>
        </Divider>
        
        <Box display="flex" justifyContent="center" flexWrap="wrap" sx={{ mt: 2 }}>
          <Button startIcon={<Google />} sx={{ mx: 1, my: 1 }} onClick={() => login({ grant_type: 'google' })}>
            Sign in with Google
          </Button>
          <Button startIcon={<Microsoft />} sx={{ mx: 1, my: 1 }} onClick={() => login({ grant_type: 'microsoft' })}>
            Sign in with Microsoft
          </Button>
          <Button startIcon={<Facebook />} sx={{ mx: 1, my: 1 }} onClick={() => login({ grant_type: 'facebook' })}>
            Sign in with Facebook
          </Button>
        </Box>
      </Paper>
    </Container>
  );
};

export default LoginPage;
